import { useEffect, useMemo, useState } from 'react';
import TemplateLoader from '../templates/TemplateLoader';
import PageRenderer from './PageRenderer';
import type { PagePayload } from '../types/pagePayload';

const CONFIGURED_CMS_ORIGIN =
  import.meta.env.VITE_CMS_ORIGIN ?? 'https://website-cms.aimpointventures.my';

type PreviewMessage = {
  type: 'CMS_PREVIEW_PAYLOAD' | 'CMS_PREVIEW_FOCUS';
  payload?: PagePayload;
  fieldPath?: string;
};

function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function getParentOrigin(): string {
  // Do not trust document.referrer as an authorization source: only the
  // configured CMS origin is permitted to supply draft preview payloads.
  return new URL(CONFIGURED_CMS_ORIGIN).origin;
}

export default function CmsPreviewPage() {
  const embedded = useMemo(() => isEmbedded(), []);
  const parentOrigin = useMemo(() => getParentOrigin(), []);
  const [payload, setPayload] = useState<PagePayload | null>(null);
  const [waitingForCms, setWaitingForCms] = useState(embedded);
  const [previewError, setPreviewError] = useState(false);


  useEffect(() => {
    if (!embedded || !payload) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Keep preview navigation inside the iframe from disrupting editing.
      if (target.closest('a[href]')) event.preventDefault();
      const paths = (payload as PagePayload & { editableFieldPaths?: string[] }).editableFieldPaths ?? [];
      const contents = (payload as PagePayload & { content?: Record<string, unknown> }).content ?? {};
      const getValue = (path: string): unknown => path.split('.').reduce<unknown>((value, part) =>
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)[part] : undefined, contents);
      const explicit = target.closest<HTMLElement>('[data-cms-field]')?.dataset.cmsField;
      const section = target.closest<HTMLElement>('[data-cms-section], [data-section], section[id], [id]');
      const key = explicit || section?.dataset.cmsSection || section?.dataset.section || section?.id || '';
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      let match = paths.find(path => path === key)
        ?? paths.find(path => path.toLowerCase().startsWith(`${key.toLowerCase()}.`) && !!key)
        ?? paths.find(path => key.toLowerCase().startsWith(`${path.toLowerCase()}.`) && !!key);
      if (!match && normalizedKey) {
        match = paths.find(path => path.toLowerCase().replace(/[^a-z0-9]/g, '').includes(normalizedKey));
      }
      // Match rendered text against actual draft values when the template has no field markers.
      if (!match) {
        const text = (target.textContent || '').trim().replace(/\s+/g, ' ');
        const candidates = [target, target.parentElement, target.parentElement?.parentElement].filter(Boolean) as Element[];
        const texts = candidates.map(node => (node.textContent || '').trim().replace(/\s+/g, ' ')).filter(t => t.length >= 3 && t.length < 250);
        if (text.length >= 3 && text.length < 250 && !texts.includes(text)) texts.unshift(text);
        match = paths.find(path => {
          const value = getValue(path);
          return typeof value === 'string' && value.trim().length >= 3 && texts.some(t => t === value.trim());
        });
        if (!match) match = paths.find(path => {
          const value = getValue(path);
          return typeof value === 'string' && value.trim().length >= 6 && texts.some(t => t.includes(value.trim()) && t.length < value.length * 3);
        });
      }
      if (match) window.parent.postMessage({ type: 'CMS_PREVIEW_SELECT', fieldPath: match }, parentOrigin);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [embedded, payload, parentOrigin]);

  useEffect(() => {
    console.log('[Renderer Preview Mode]', {
      embedded,
      parentOrigin,
      configuredCmsOrigin: CONFIGURED_CMS_ORIGIN,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      behavior: embedded
        ? 'CMS iframe preview; requesting CMS_PREVIEW_PAYLOAD'
        : 'Direct preview; PageRenderer will call API /web',
    });
  }, [embedded, parentOrigin]);

  useEffect(() => {
    if (!embedded) return;

    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      if (event.source !== window.parent) return;
      if (event.origin !== parentOrigin) {
        console.warn('[Renderer Preview] Ignored message from unexpected origin', {
          received: event.origin,
          expected: parentOrigin,
        });
        return;
      }
      if (event.data?.type === 'CMS_PREVIEW_FOCUS' && typeof event.data.fieldPath === 'string') {
        const key = event.data.fieldPath.split(/[.\[]/)[0];
        // Prefer explicit section markers provided by the template. Never use
        // arbitrary CSS selectors built from user-controlled schema values.
        const candidates = document.querySelectorAll<HTMLElement>('[data-cms-section], [data-section], section[id], [id]');
        const target = Array.from(candidates).find(el =>
          [el.dataset.cmsSection, el.dataset.section, el.id].some(v => v?.toLowerCase() === key.toLowerCase())
        );
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (event.data?.type !== 'CMS_PREVIEW_PAYLOAD' || !event.data.payload) return;

      console.log('[Renderer Preview] CMS_PREVIEW_PAYLOAD received');
      setPayload(event.data.payload);
      setWaitingForCms(false);
      setPreviewError(false);
      window.parent.postMessage({ type: 'CMS_PREVIEW_RECEIVED' }, parentOrigin);
    };

    window.addEventListener('message', onMessage);

    console.log('[Renderer Preview] Sending CMS_PREVIEW_READY', { parentOrigin });
    // The CMS may mount its message listener after the iframe sends its first READY.
    // Retry the handshake until a valid payload arrives, without weakening origin checks.
    const readyInterval = window.setInterval(() => {
      window.parent.postMessage({ type: 'CMS_PREVIEW_READY' }, parentOrigin);
    }, 500);
    window.parent.postMessage({ type: 'CMS_PREVIEW_READY' }, parentOrigin);

    // Fail closed: a missing CMS payload must never be mistaken for a draft preview.
    const fallbackTimer = window.setTimeout(() => {
      setWaitingForCms((stillWaiting) => {
        if (stillWaiting) {
          console.warn(
            '[Renderer Preview] CMS payload not received; refusing to show published content as draft preview'
          );
          setPreviewError(true);
        }
        return false;
      });
    }, 15000);

    return () => {
      window.clearInterval(readyInterval);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('message', onMessage);
    };
  }, [embedded, parentOrigin]);

  if (!embedded) return <PageRenderer />;
  if (payload) return <TemplateLoader payload={payload} />;

  if (waitingForCms) {
    return (
      <div className="renderer-state">
        <div className="renderer-state__spinner" />
        <p>Preparing preview…</p>
      </div>
    );
  }

  if (previewError) {
    return (
      <main role="alert" className="renderer-state">
        <h1>Preview unavailable</h1>
        <p>The CMS did not provide a preview payload. Reopen the preview from the CMS.</p>
      </main>
    );
  }

  return null;
}
